/**
 * TokenStatusIndicator Component
 *
 * Displays token status and allows manual refresh.
 * Shows warnings when token is about to expire.
 */

import { useTokenStatus } from '../../hooks/useTokenStatus';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';

export const TokenStatusIndicator = () => {
  const { isAuthenticated } = useAuth();
  const {
    isExpired,
    isExpiringSoon,
    minutesRemaining,
    isRefreshing,
    lastRefreshError,
    manualRefresh,
  } = useTokenStatus();
  const { success, error: showError } = useToast();

  // Don't show anything if user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Don't show anything if token is healthy
  if (!isExpired && !isExpiringSoon && !lastRefreshError) {
    return null;
  }

  const handleManualRefresh = async () => {
    const result = await manualRefresh();

    if (result) {
      success('Session refreshed successfully!');
    } else {
      showError('Failed to refresh session. Please sign in again.');
    }
  };

  // Token expired - critical
  if (isExpired) {
    return (
      <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 max-w-md">
        <div className="bg-red-50 dark:bg-red-900/30 border-l-4 border-red-500 p-4 rounded-r-lg shadow-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Session Expired
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                Your session has expired. Click refresh to continue or sign in again.
              </p>
              <div className="mt-3">
                <button
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isRefreshing ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Refreshing...
                    </span>
                  ) : (
                    'Refresh Session'
                  )}
                </button>
              </div>
            </div>
            <div className="ml-3 flex-shrink-0">
              {/* Close button could be added here */}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Token expiring soon - warning
  if (isExpiringSoon) {
    return (
      <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 max-w-md">
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 p-4 rounded-r-lg shadow-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Session Expiring Soon
              </h3>
              <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                Your session will expire in {minutesRemaining} minute{minutesRemaining !== 1 ? 's' : ''}.
                It will refresh automatically.
              </p>
              {lastRefreshError && (
                <div className="mt-2">
                  <button
                    onClick={handleManualRefresh}
                    disabled={isRefreshing}
                    className="text-sm text-yellow-800 dark:text-yellow-200 underline hover:no-underline disabled:opacity-50"
                  >
                    Refresh now
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Refresh error - show retry option
  if (lastRefreshError) {
    return (
      <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 max-w-md">
        <div className="bg-orange-50 dark:bg-orange-900/30 border-l-4 border-orange-500 p-4 rounded-r-lg shadow-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-orange-800 dark:text-orange-200">
                Session Refresh Failed
              </h3>
              <p className="mt-1 text-sm text-orange-700 dark:text-orange-300">
                We couldn't refresh your session automatically. Please try again.
              </p>
              <div className="mt-3">
                <button
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                  className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isRefreshing ? 'Refreshing...' : 'Try Again'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
